import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import './AccountShareJobs.less';
import { jobsSelectors } from '../../../../redux/jobs';
import { useDispatch, useSelector } from 'react-redux';
import Loading from '../../../../components/common/Loading/Loading';
import Table, { AggregateValue, SelectionGroupsMode } from '../../../../components/envago/Table/Table';
import { pluralHelper, splitJoin } from '../../../../helper/TextHelper';
import { resolveMeterType } from '../../../../helper/MeterHelper';
import EnergyIcon from '../../../../components/envago/EnergyIcon/EnergyIcon';
import { JobType } from '../../../../redux/jobs/jobs.types';
import Icon from '../../../../components/envago/Icon/Icon';
import Empty from '../../../../components/envago/Empty/Empty';
import Dropdown from '../../../../components/envago/Dropdown/Dropdown';
import Tooltip from '../../../../components/envago/Tooltip/Tooltip';
import { ConfirmDialog, ConfirmContentType } from '../../../../components/envago/ConfirmDialog';
import { hasMultipleValues } from '../../../../helper/GroupHelper';
import { Trans, useTranslation } from 'react-i18next';
import { formatDate } from '../../../../helper/DateHelper';
import { sharesSelectors } from '../../../../redux/shares';
import { ColumnDef, RowSelectionState } from '@tanstack/react-table';
import { IndeterminateCheckbox } from '../../../../components/envago/IndeterminateCheckbox';
import { applicationSelectors } from '../../../../redux/application';
import { assignmentPresetsActions, assignmentPresetsSelectors } from '../../../../redux/assignmentPresets';
import { matchPreset, MeterChangeInfo } from '../../../../helper/AssignmentPresetMatcher';
import { AssignmentPresetType } from '../../../../redux/login/login.types';
import { applyColumnConfig, hasDataColumn, hasGroupableColumn } from '../../../common/JobTable/applyColumnConfig';
import { hasMultipleVisibleFieldValues, hasVisibleField } from '../../../../fieldVisibility/fieldVisibility';
import { useFieldVisibility } from '../../../../fieldVisibility/useFieldVisibility';

interface AccountShareJobPropTypes {
    accountId?: string;
    description?: ReactNode;
    notice?: ReactNode;
    selectionGroups?: SelectionGroupsMode;
    filterJob?: (job: JobType) => boolean;
    onSelectionChanged?: (ids: string[], jobs: JobType[], groups: { name: string; data: JobType[] }[]) => void;
    enableAssignmentPresets?: boolean;
    onPresetApplied?: (preset: AssignmentPresetType | undefined) => void;
}

const AccountShareJobs = ({
    accountId,
    description,
    notice,
    selectionGroups,
    filterJob,
    onSelectionChanged,
    enableAssignmentPresets,
    onPresetApplied,
}: AccountShareJobPropTypes) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isLoading = useSelector(jobsSelectors.isLoading);

    // Diese Tabelle ist in beiden Verwendungen (Aufträge teilen, Auftragsauswahl
    // im Export-Assistenten) dieselbe, deshalb kein Prop.
    const jobTableConfig = useSelector(applicationSelectors.getJobTableConfig('jobSelection'));
    const visibility = useFieldVisibility('jobSelection');

    // Bestandteile zusammengesetzter Zellen: field(key, value) liefert
    // undefined, wenn die Config den Bestandteil weggenommen hat. splitJoin
    // filtert das ohnehin weg, die Zellen bleiben dadurch lesbar.
    //
    // Kein eigenes useMemo mehr: pick gehört zum visibility-Objekt, das
    // useFieldVisibility schon memoisiert.
    const field = visibility.pick;

    const allJobs = useSelector(jobsSelectors.getJobs);
    const accountJobs = useSelector(jobsSelectors.getShareableJobsByAccountId(accountId ?? ''));
    const [baseJobs] = useState(accountId ? accountJobs : allJobs);
    const jobs = useMemo(
        () => (filterJob ? baseJobs.filter(filterJob) : baseJobs),
        [baseJobs, filterJob]
    );
    const sharedJobsForAccount = useSelector(sharesSelectors.getSharedJobIdsForAccount(accountId ?? ''));
    const [sharedJobs] = useState(accountId ? sharedJobsForAccount : []);

    const presetsEnabled = useSelector(applicationSelectors.isAssignmentPresetsAllowed);
    const presets = useSelector(assignmentPresetsSelectors.getPresetsForAccount(accountId ?? ''));
    const showPresets = !!enableAssignmentPresets && presetsEnabled && presets.length > 0;

    useEffect(() => {
        if (accountId && presetsEnabled && enableAssignmentPresets) {
            dispatch(assignmentPresetsActions.fetchPresets(accountId));
        }
    }, [accountId, presetsEnabled, enableAssignmentPresets, dispatch]);

    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [meterChangeInfoById, setMeterChangeInfoById] = useState<Map<string, MeterChangeInfo>>(new Map());
    const [selectedPresetId, setSelectedPresetId] = useState<string | undefined>(undefined);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const applyPreset = useCallback(
        (presetId: string) => {
            const preset = presets.find((p) => p.id === presetId);
            if (!preset) return;
            const { matchedIds, meterChangeInfos } = matchPreset(jobs, preset);
            setRowSelection(Object.fromEntries(matchedIds.map((id) => [id, true])));
            setMeterChangeInfoById(new Map(meterChangeInfos.map((info) => [info.jobId, info])));
            onPresetApplied?.(preset);
        },
        [presets, jobs, onPresetApplied]
    );

    const presetOptions = useMemo(
        () =>
            [...presets]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((p) => ({ id: p.id, value: p.id, name: p.name })),
        [presets],
    );

    const selectedPreset = presets.find((p) => p.id === selectedPresetId);

    const handlePresetChange = useCallback(
        (presetId?: string) => {
            setSelectedPresetId(presetId);
            if (presetId) applyPreset(presetId);
        },
        [applyPreset],
    );

    // Applying a preset has three effects (row selection, meter-change markers, parent's appliedPreset).
    // Resetting must undo all three, otherwise a cleared preset leaks into the next step (prefilled name/emails).
    const resetPresetApplication = useCallback(() => {
        setSelectedPresetId(undefined);
        setRowSelection({});
        setMeterChangeInfoById(new Map());
        onPresetApplied?.(undefined);
    }, [onPresetApplied]);

    const handleResetSelection = resetPresetApplication;

    const handleConfirmDelete = useCallback(() => {
        if (selectedPreset) {
            dispatch(assignmentPresetsActions.deletePreset(accountId ?? '', selectedPreset.id));
            resetPresetApplication();
        }
        setDeleteDialogOpen(false);
    }, [dispatch, accountId, selectedPreset, resetPresetApplication]);

    // The banner must reflect the *current* selection, not the apply-time snapshot: count only the
    // meter-changed jobs that are still selected, so deselecting them lowers the count and the
    // banner disappears once none remain.
    const selectedMeterChangeCount = useMemo(
        () => Array.from(meterChangeInfoById.keys()).filter((id) => rowSelection[id]).length,
        [meterChangeInfoById, rowSelection],
    );

    const showColumns = useMemo(() => {
        return {
            'meter.meterAddress.objectKey': hasMultipleValues(jobs, 'meter.meterAddress.objectKey'),
            'customer.customerNumber': hasMultipleValues(jobs, 'customer.customerNumber'),
            // Die Automatik zählt nur die Bestandteile, die die Config übrig
            // lässt: unterscheidet sich allein der ausgeblendete Nachname,
            // stünde in der Spalte sonst bei jeder Zeile derselbe Vorname.
            'customer.name': hasMultipleVisibleFieldValues(jobs, visibility, [
                'customer.firstname',
                'customer.lastname',
            ]),
            // Straße, PLZ und Ort hängen GEMEINSAM an diesem Schalter. Deshalb
            // wird hier nur der Bestandteil „Hausnummer" gefiltert und keiner
            // der drei Spalten-Keys: würde ihr Ausblenden den Schalter kippen,
            // fielen alle drei Spalten weg und die Sammelspalte träte an ihre
            // Stelle — mit demselben immer gleichen Inhalt.
            'meter.meterAddress':
                hasMultipleValues(jobs, 'meter.meterAddress.street') ||
                hasMultipleVisibleFieldValues(jobs, visibility, ['meter.meterAddress.houseNumber']) ||
                hasMultipleValues(jobs, 'meter.meterAddress.zip') ||
                hasMultipleValues(jobs, 'meter.meterAddress.city'),
            // gstack-shortcut(dec-aaa051da): vier der folgenden Schalter liest
            // niemand, sie bleiben bewusst stehen — upgrade, wenn jemand die
            // Automatik pro Adressspalte wirklich trennen will oder die
            // showColumns-Berechnung im Profiler auffällt.
            //
            // Straße, PLZ und Ort hängen gemeinsam an
            // showColumns['meter.meterAddress'] (Zeilen 222/237/247), die
            // Ablesefrist-Spalte (Zeile 484) hat gar keinen Guard. Die drei
            // Einzelschalter und 'jobConstraints.latestExecution' werden deshalb
            // nirgends gelesen. NICHT verdrahten: das würde heutiges Verhalten
            // ändern, und die Config darf ohnehin nur wegnehmen.
            //
            // Achtung beim Lesen von Task 7: showColumns['meter.meterAddress.street']
            // (tot) steht dort neben showStreet (aus der Config, wirksam).
            'meter.meterAddress.street': hasMultipleValues(jobs, 'meter.meterAddress.street') || hasMultipleValues(jobs, 'meter.meterAddress.houseNumber'),
            'meter.meterAddress.zip': hasMultipleValues(jobs, 'meter.meterAddress.zip'),
            'meter.meterAddress.city': hasMultipleValues(jobs, 'meter.meterAddress.city'),
            'meter.meterAddress.cityDistrict': hasMultipleValues(jobs, 'meter.meterAddress.cityDistrict'),
            'customer.company': hasMultipleValues(jobs, 'customer.company'),
            // 'meterReadingJob.partner.name' war ein i18n-Schlüssel, kein Feld am
            // Auftrag: hasMultipleValues löst den Pfad mit dotProp.get auf
            // (GroupHelper.ts:17), job.meterReadingJob gibt es nicht — der zweite
            // Operand war also immer false. Die Gruppe erschien nur bei
            // unterschiedlichen Partner-NUMMERN. Geprüft werden die Felder, die
            // die Namensspalte wirklich rendert (Zeile 468).
            //
            // Die Partner-Nr. ist eine SPALTE und wird deshalb nicht gefiltert:
            // ihr Ausblenden regelt die Spalten-Pipeline. Gefiltert werden nur
            // die drei Bestandteile der Namenszelle.
            partner:
                hasMultipleValues(jobs, 'partner.identifier') ||
                hasMultipleVisibleFieldValues(jobs, visibility, [
                    'partner.company',
                    'partner.lastname',
                    'partner.firstname',
                ]),
            'jobConstraints.latestExecution': hasMultipleValues(jobs, 'jobConstraints.latestExecution'),
        };
    }, [jobs, visibility]);

    const isSharedSort = useCallback(
        (rowA: any, rowB: any) => {
            const idA = rowA.original.id;
            const idB = rowB.original.id;
            const a = sharedJobs.includes(idA);
            const b = sharedJobs.includes(idB);

            return a > b ? 1 : a < b ? -1 : 0;
        },
        [sharedJobs],
    );

    const meterTypeSort = useCallback((rowA: any, rowB: any) => {
        const a = resolveMeterType(rowA.original);
        const b = resolveMeterType(rowB.original);

        return a > b ? 1 : a < b ? -1 : 0;
    }, []);

    const statusSort = useCallback((rowA: any, rowB: any) => {
        const a = rowA.original.done;
        const b = rowB.original.done;

        return a > b ? 1 : a < b ? -1 : 0;
    }, []);

    // Sammelspalte „Kunde" — wer trägt zu ihr bei?
    //
    //   customerNumber      company        name
    //        │                 │             │
    //        │                 └──────┬──────┘
    //        │                        ▼
    //        │              beide ausgeblendet? → []  (Gruppe entfällt)
    //        │              eines übrig?        → customer.summary
    //        │                                     aus den Resten gebaut
    //        └─ gehört NIE zur Sammelspalte: sie ausblenden darf den
    //           Kundennamen nicht mitnehmen (Spec, Entscheidung 5)
    //
    // Verarbeitungsreihenfolge (Spec): erst die Automatik, dann die Config —
    // und ERST DANACH entscheidet sich, ob eine Sammelspalte entsteht. Deshalb
    // fragt dieser Block isHidden selbst, statt sich auf applyColumnConfig
    // am Ende zu verlassen: sonst fiele die Sammelspalte weg, obwohl von ihr
    // noch etwas übrig ist.
    const customerColumns = useMemo(() => {
        const showCompany = !visibility.isHidden('customer.company');
        // „Der Name ist zu sehen" = die Angabe ist nicht ausgeblendet UND von
        // ihren Bestandteilen ist mindestens einer übrig. Der Rückfall auf
        // customer.summary erbt das über showName — deshalb genügt diese eine
        // Zeile für Spalte UND Sammelspalte.
        const showName =
            !visibility.isHidden('customer.name') &&
            hasVisibleField(visibility, ['customer.lastname', 'customer.firstname']);

        const columns = [
            showColumns['customer.customerNumber'] && !visibility.isHidden('customer.customerNumber') && {
                id: 'customer.customerNumber',
                header: t('meterReadingJob.customer.customernumber'),
                accessorFn: (row: JobType) => row.customer.customerNumber,
                aggregationFn: 'uniqueCount',
                aggregatedCell: ({ getValue }: any) => (
                    <AggregateValue>
                        {t('meterReadingJob.aggregation.customernumber', {
                            count: getValue(),
                        })}
                    </AggregateValue>
                ),
            },
            showColumns['customer.company'] && showCompany && {
                id: 'customer.company',
                header: t('meterReadingJob.customer.company'),
                accessorFn: (row: JobType) => row.customer.company,
            },
            showColumns['customer.name'] && showName && {
                id: 'customer.name',
                header: t('meterReadingJob.customer.name'),
                accessorFn: (row: JobType) =>
                    splitJoin(
                        ', ',
                        field('customer.lastname', row?.customer?.lastname),
                        field('customer.firstname', row?.customer?.firstname),
                    ),
            },
        ].filter(Boolean);

        if (columns.length > 0) return columns;

        // Rückfall: die Sammelspalte wird nur aus den nicht ausgeblendeten
        // Bestandteilen gebaut. Die Kundennummer gehört nie dazu — sie darf den
        // Kundennamen also auch nicht mitnehmen.
        if (!showCompany && !showName) return [];

        return [
            {
                id: 'customer.summary',
                header: t('meterReadingJob.customer.name'),
                canSort: false,
                accessorFn: (row: JobType) => {
                    const company = showCompany ? row.customer.company : undefined;
                    const name = showName
                        ? splitJoin(
                              ', ',
                              field('customer.lastname', row?.customer?.lastname),
                              field('customer.firstname', row?.customer?.firstname),
                          )
                        : undefined;

                    return company ? company : name;
                },
            },
        ];
    }, [showColumns, t, visibility, field]);

    const addressColumns = useMemo(() => {
        const showStreet = !visibility.isHidden('meter.meterAddress.street');
        const showZip = !visibility.isHidden('meter.meterAddress.zip');
        const showCity = !visibility.isHidden('meter.meterAddress.city');
        const showCityDistrict = !visibility.isHidden('meter.meterAddress.cityDistrict');

        const columns = [
            showColumns['meter.meterAddress'] && showStreet && {
                id: 'meter.meterAddress.street',
                header: t('meterReadingJob.address.street'),
                canSort: true,
                aggregationFn: 'uniqueCount',
                accessorFn: (row: JobType) =>
                    splitJoin(
                        ' ',
                        row?.meter.meterAddress?.street,
                        field('meter.meterAddress.houseNumber', row?.meter.meterAddress?.houseNumber),
                    ),
                aggregatedCell: ({ getValue }: any) => <AggregateValue>{t('meterReadingJob.aggregation.street', { count: getValue() })}</AggregateValue>,
                cell: (props: any) => {
                    if (props.cell.isGrouped) {
                        return props.cell.row.groupByVal;
                    }

                    return splitJoin(
                        ' ',
                        props.row.original?.meter.meterAddress?.street,
                        field('meter.meterAddress.houseNumber', props.row.original?.meter.meterAddress?.houseNumber),
                    );
                },
            },
            showColumns['meter.meterAddress'] && showZip && {
                id: 'meter.meterAddress.zip',
                header: t('meterReadingJob.address.zip'),
                size: 60,
                accessorFn: (row: JobType) => row.meter.meterAddress?.zip,
                aggregationFn: 'uniqueCount',
                aggregatedCell: ({ getValue }: any) => {
                    return <AggregateValue>{t('meterReadingJob.aggregation.zip', { count: getValue() })}</AggregateValue>;
                },
            },
            showColumns['meter.meterAddress'] && showCity && {
                id: 'meter.meterAddress.city',
                header: t('meterReadingJob.address.city'),
                accessorFn: (row: JobType) => row.meter.meterAddress?.city,
                aggregationFn: 'uniqueCount',
                aggregatedCell: ({ getValue }: any) => {
                    return <AggregateValue>{t('meterReadingJob.aggregation.city', { count: getValue() })}</AggregateValue>;
                },
            },
            showColumns['meter.meterAddress.cityDistrict'] && showCityDistrict && {
                id: 'meter.meterAddress.cityDistrict',
                header: t('meterReadingJob.address.cityDistrict'),
                canSort: false,
                accessorFn: (row: JobType) => row.meter.meterAddress?.cityDistrict,
                aggregationFn: 'uniqueCount',
                aggregatedCell: ({ getValue }: any) => {
                    return (
                        <AggregateValue>
                            {t('meterReadingJob.aggregation.cityDistrict', {
                                count: getValue(),
                            })}
                        </AggregateValue>
                    );
                },
            },
        ].filter(Boolean);

        if (columns.length > 0) return columns;

        // Rückfall: die Zähleranschrift wird nur aus den nicht ausgeblendeten
        // Bestandteilen zusammengesetzt und entfällt, wenn keiner übrig bleibt.
        if (!showStreet && !showZip && !showCity && !showCityDistrict) return [];

        return [
            {
                id: 'meter.meterAddress.full',
                header: t('meterReadingJob.address.fullAddress'),
                accessorFn: (row: JobType) => {
                    const street = showStreet
                        ? splitJoin(
                              ' ',
                              row?.meter.meterAddress?.street,
                              field('meter.meterAddress.houseNumber', row?.meter.meterAddress?.houseNumber),
                          )
                        : undefined;
                    const zip = showZip ? row?.meter.meterAddress?.zip : undefined;
                    const city = showCity ? row?.meter.meterAddress?.city : undefined;
                    const district = showCityDistrict ? row?.meter.meterAddress?.cityDistrict : undefined;
                    // Die Klammer trägt den Ortsteil nur, wenn auch der Ort
                    // dasteht. Blendet eine Config Straße, PLZ und Ort aus,
                    // stünde in der „Zähleranschrift" sonst nur „(Findorff)" —
                    // eine Klammer ohne Bezug.
                    const cityDistrict = district ? (city ? `(${district})` : district) : undefined;

                    return splitJoin(' ', street, zip, city, cityDistrict);
                },
            },
        ];
    }, [showColumns, t, visibility, field]);

    const columns = useMemo<ColumnDef<JobType>[]>(
        () =>
            applyColumnConfig([
                {
                    id: 'select',
                    canSort: false,
                    enableGrouping: false,
                    size: 40,
                    header: ({ table }: any) => (
                        <div className="px-1">
                            <IndeterminateCheckbox
                                {...{
                                    checked: table.getIsAllRowsSelected(),
                                    indeterminate: table.getIsSomeRowsSelected(),
                                    onChange: table.getToggleAllRowsSelectedHandler(),
                                }}
                                aria-label={'Selektion alle'}
                            />
                        </div>
                    ),
                    cell: ({ row }: any) => {
                        return (
                            <div className="px-1">
                                <IndeterminateCheckbox
                                    {...{
                                        checked: row.getIsSelected(),
                                        indeterminate: row.getIsSomeSelected(),
                                        onChange: row.getToggleSelectedHandler(),
                                    }}
                                    aria-label={'Selektion'}
                                />
                            </div>
                        );
                    },
                    // Gruppenzeilen haben ihre eigene (von den Kindern abgeleitete)
                    // Checkbox in der Gruppierungszelle — der Aggregations-Fallback
                    // würde hier eine zweite, an tanstacks veraltetem Gruppen-Eintrag
                    // hängende Checkbox rendern (#313).
                    aggregatedCell: () => null,
                },
                {
                    header: t('management.accounts.share.tableColumns.isShared'),
                    id: 'job.isShared',
                    canSort: true,
                    sortType: isSharedSort,
                    sortInverted: true,
                    enableGrouping: false,
                    size: 60,
                    cell: SharedCell,
                },
                {
                    header: t('meterReadingJob.type'),
                    id: 'meter.meterType',
                    canSort: true,
                    sortType: meterTypeSort,
                    enableGrouping: false,
                    size: 40,
                    cell: (props: any) => {
                        const meterType = resolveMeterType(props.row.original.meter);

                        return <EnergyIcon iconClassName={'h-5 w-5'} meterType={meterType} />;
                    },
                },
                {
                    header: t('meterReadingJob.status'),
                    id: 'job.status',
                    size: 50,
                    sortType: statusSort,
                    sortInverted: true,
                    enableGrouping: false,
                    accessor: 'done',
                    accessorFn: (row: JobType) => row.done,
                    aggregate: 'count',
                    cell: ({ getValue }: any) => {
                        return (
                            <div>
                                {getValue() ? (
                                    <Icon aria={{'aria-label': 'Kreis mit Haken Symbol', role: 'option'}} path={Icon.Path.mdiCheckCircleOutline} className={'h-5 text-green-500'} />
                                ) : (
                                    <Icon aria={{'aria-label': 'nicht gewählter Kreis Symbol'}} path={Icon.Path.mdiCheckboxBlankCircleOutline} className={'h-5 text-gray-300'} />
                                )}
                            </div>
                        );
                    },
                },
                {
                    header: t('meterReadingJob.meter'),
                    id: 'meter.meterNumber',
                    canSort: true,
                    enableGrouping: false,
                    accessorKey: 'meter.meterNumber',
                    accessorFn: (row: JobType) => row.meter.meterNumber,
                    aggregationFn: 'uniqueCount',
                    aggregatedCell: ({ getValue }: any) => <AggregateValue>{t('meterReadingJob.aggregation.meter', { count: getValue() })}</AggregateValue>,
                    cell: ({ row }: any) => {
                        const info = meterChangeInfoById.get(row.original.id);
                        return (
                            <div className={'flex items-center gap-1'}>
                                <span>{row.original.meter?.meterNumber}</span>
                                {info && (
                                    <Tooltip
                                        width={'320px'}
                                        variant={'light'}
                                        tooltipText={
                                            <div className={'flex flex-col gap-2'}>
                                                <div className={'flex items-center gap-1 text-amber-600 font-medium'}>
                                                    <Icon path={Icon.Path.mdiAlertOutline} className={'h-4'} />
                                                    <span>{t('management.accounts.share.assignmentPresets.meterChangedTitle')}</span>
                                                </div>
                                                <p className={'text-gray-600'}>
                                                    {t('management.accounts.share.assignmentPresets.meterChangedTooltipIntro', {
                                                        meLoId: info.meLoId,
                                                    })}
                                                </p>
                                                <div className={'flex flex-col gap-0.5'}>
                                                    <div className={'flex gap-3'}>
                                                        <span className={'w-36 flex-shrink-0 text-gray-500'}>
                                                            {t('management.accounts.share.assignmentPresets.meterChangedStoredLabel')}
                                                        </span>
                                                        <span className={'font-mono break-all'}>
                                                            {info.storedMeterNumbers.join(', ') || '—'}
                                                        </span>
                                                    </div>
                                                    <div className={'flex gap-3'}>
                                                        <span className={'w-36 flex-shrink-0 text-gray-500'}>
                                                            {t('management.accounts.share.assignmentPresets.meterChangedCurrentLabel')}
                                                        </span>
                                                        <span className={'font-mono font-medium text-gray-800 break-all'}>
                                                            {info.currentMeterNumber ?? '—'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        }
                                    >
                                        <span className={'flex items-center gap-1 text-amber-500 text-xs'}>
                                            <Icon
                                                aria={{ 'aria-label': t('management.accounts.share.assignmentPresets.meterChanged') }}
                                                path={Icon.Path.mdiAlertOutline}
                                                className={'h-4'}
                                            />
                                            <span>{t('management.accounts.share.assignmentPresets.meterChangedLabel')}</span>
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                        );
                    },
                },
                showColumns['meter.meterAddress.objectKey'] && {
                    id: 'meter.meterAddress.objectKey',
                    header: t('meterReadingJob.objectKey'),
                    accessorFn: (row: JobType) => row.meter.meterAddress?.objectKey,
                    aggregationFn: 'uniqueCount',
                    aggregatedCell: ({ getValue }: any) => <AggregateValue>{t('meterReadingJob.aggregation.objectKey', { count: getValue() })}</AggregateValue>,
                },
                showColumns['partner'] && {
                    id: 'meterReadingJob.partner.title',
                    header: t('meterReadingJob.partner.title'),
                    enableGrouping: false,
                    columns: [
                        {
                            id: 'partner.identifier',
                            header: t('meterReadingJob.partner.identifier'),
                            accessorKey: 'partner.identifier',
                            accessorFn: (row: JobType) => row.partner?.identifier,
                            aggregationFn: 'uniqueCount',
                            aggregatedCell: ({ getValue }: any) => (
                                <AggregateValue>
                                    {getValue()} {pluralHelper(getValue(), 'Partner', 'Partner')}
                                </AggregateValue>
                            ),
                        },
                        // Bleibt von Firma, Nachname und Vorname keiner übrig,
                        // entfällt die Spalte. Ist danach auch die Partner-Nr.
                        // ausgeblendet, nimmt applyColumnConfig die kinderlose
                        // Gruppe mit — das kann es schon.
                        hasVisibleField(visibility, ['partner.company', 'partner.lastname', 'partner.firstname']) && {
                            id: 'partner.name',
                            header: t('meterReadingJob.partner.name'),
                            accessorFn: (row: any) =>
                                splitJoin(
                                    ' ',
                                    field('partner.company', row?.partner?.company),
                                    splitJoin(
                                        ', ',
                                        field('partner.lastname', row?.partner?.lastname),
                                        field('partner.firstname', row?.partner?.firstname),
                                    ),
                                ),
                        },
                    ]
                        .filter((a) => a)
                        .map((a: any) => a as ColumnDef<JobType>),
                },
                {
                    id: 'meterReadingJob.customer.title',
                    header: t('meterReadingJob.customer.title'),
                    enableGrouping: false,
                    columns: customerColumns,
                },
                {
                    id: 'meterReadingJob.address.title',
                    header: t('meterReadingJob.address.title'),
                    enableGrouping: false,
                    columns: addressColumns,
                },
                {
                    id: 'jobConstraints.latestExecution',
                    header: t('meterReadingJob.jobConstraints.latestExecution'),
                    accessor: 'jobConstraints.latestExecution',
                    accessorFn: (row: JobType) => {
                        if (!row.jobConstraints?.latestExecution) return null;
                        return formatDate(row.jobConstraints?.latestExecution);
                    },
                    aggregationFn: 'uniqueCount',
                    aggregatedCell: ({ getValue }: any) => {
                        return (
                            <AggregateValue>
                                {t('meterReadingJob.aggregation.latestExecution', {
                                    count: getValue(),
                                })}
                            </AggregateValue>
                        );
                    },
                },
            ]
                .filter((a) => a)
                .map((a: any) => a as ColumnDef<JobType>),
                visibility,
                jobTableConfig,
                'jobSelection',
            ),

        [isSharedSort, meterTypeSort, showColumns, statusSort, t, customerColumns, addressColumns, meterChangeInfoById, visibility, jobTableConfig, field],
    );

    // Gruppierbar ist, was kein `enableGrouping: false` trägt — heute Objekt,
    // Partner, Kunde, Adresse und Ablesefrist. Bleibt davon nichts übrig,
    // entfällt im Hinweistext nur der Gruppieren-Teil.
    const showGroupingHint = useMemo(() => hasGroupableColumn(columns), [columns]);

    if (isLoading) return <Loading text={t('management.accounts.share.loading')} />;

    return (
        <>
            <div className="account-share-jobs text-sm flex flex-col max-h-full h-full min-h-0">
                <div className="account-share-jobs--copy flex-shrink-0">
                    {description ?? <p>{t('management.accounts.share.description')}</p>}
                    {showGroupingHint ? (
                        <p>
                            <Trans i18nKey={'management.accounts.share.hint'}>
                                <i>Tipp:</i> Klicke auf die Spalten-Namen um die Tabelle zu sortieren. Mit einem Klick auf{' '}
                                <Icon aria={{'aria-label': 'Gruppierungssymbol'}} className={'h-4 inline'} path={Icon.Path.mdiSelectGroup} />
                                {` `}
                                kannst du Aufträge gruppieren.
                            </Trans>
                        </p>
                    ) : (
                        <p>
                            <Trans i18nKey={'management.accounts.share.hintSortOnly'}>
                                <i>Tipp:</i> Klicke auf die Spalten-Namen um die Tabelle zu sortieren.
                            </Trans>
                        </p>
                    )}
                    {notice}
                    {showPresets && (
                        <>
                            <div className="assignment-preset-select flex items-center gap-2">
                                <span className="font-medium">{t('management.accounts.share.assignmentPresets.label')}</span>
                                {/* -mb-1 cancels the Dropdown button's built-in mb-1 (compact) so it stays vertically centered in this flex row */}
                                <div className="w-56 -mb-1">
                                    <Dropdown
                                        values={presetOptions}
                                        value={selectedPresetId}
                                        onChange={handlePresetChange}
                                        placeholder={t('management.accounts.share.assignmentPresets.selectPlaceholder')}
                                        compact={true}
                                    />
                                </div>
                                {selectedPreset && (
                                    <>
                                        <button
                                            type="button"
                                            className="text-gray-500 hover:text-primary"
                                            aria-label={t('management.accounts.share.assignmentPresets.resetLabel')}
                                            onClick={handleResetSelection}
                                        >
                                            <Icon path={Icon.Path.mdiUndo} className={'h-5'} />
                                        </button>
                                        <button
                                            type="button"
                                            className="text-gray-500 hover:text-red-500"
                                            aria-label={t('management.accounts.share.assignmentPresets.deleteLabel')}
                                            onClick={() => setDeleteDialogOpen(true)}
                                        >
                                            <Icon path={Icon.Path.mdiTrashCanOutline} className={'h-5'} />
                                        </button>
                                    </>
                                )}
                            </div>
                            <ConfirmDialog
                                open={deleteDialogOpen}
                                type={ConfirmContentType.WARNING}
                                showIcon={true}
                                title={t('management.accounts.share.assignmentPresets.deleteLabel')}
                                content={t('management.accounts.share.assignmentPresets.deleteConfirm', {
                                    name: selectedPreset?.name ?? '',
                                })}
                                okText={t('management.accounts.share.assignmentPresets.deleteConfirmOk')}
                                cancelText={t('common.dialog.abort')}
                                onOk={handleConfirmDelete}
                                onCancel={() => setDeleteDialogOpen(false)}
                            />
                        </>
                    )}
                    {selectedMeterChangeCount > 0 && (
                        <div
                            role="status"
                            className="mt-4 flex items-center gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-700"
                        >
                            <Icon path={Icon.Path.mdiAlertOutline} className={'h-4 flex-shrink-0 text-amber-600'} />
                            <span>
                                {t('management.accounts.share.assignmentPresets.meterChangedNotice', {
                                    count: selectedMeterChangeCount,
                                })}
                            </span>
                        </div>
                    )}
                </div>
                <div className={'flex-1 min-h-0 max-h-full max-w-full'}>
                    {hasDataColumn(columns) ? (
                        <Table
                            fullWidth={true}
                            groupable={true}
                            selectable={true}
                            className={''}
                            columns={columns}
                            selectionGroups={selectionGroups}
                            rowSelection={rowSelection}
                            onRowSelectionChange={setRowSelection}
                            onSelectionChange={(selection, rows, groups) => {
                                if (onSelectionChanged) {
                                    onSelectionChanged(selection, rows, groups);
                                }
                            }}
                            empty={
                                <div>
                                    <Empty>{t('management.accounts.share.empty')}</Empty>
                                </div>
                            }
                            data={jobs}
                        />
                    ) : (
                        // Der Hinweis ersetzt nur die TABELLE, nicht die
                        // Komponente: darüber stehen die Zuordnungs-Vorlagen und
                        // der Bedienhinweis. Ein früher Ausstieg nähme dem
                        // Nutzer die Vorlagen weg, und er könnte nichts mehr
                        // teilen.
                        <div className={'text-sm text-gray-500'}>
                            {t('management.accounts.share.tableColumns.allHidden')}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

const SharedCell = (props: any) => {
    const { id, login } = props.row.original as JobType;
    const isShared = useSelector(sharesSelectors.isJobInAccountShared(login, id));

    return isShared ? (
        <Icon aria={{'aria-label': 'geteilt Symbol'}} path={Icon.Path.mdiCheckCircle} className={'h-5 text-primary'} />
    ) : (
        <Icon aria={{'aria-label': 'nicht geteilt Symbol'}} path={Icon.Path.mdiClose} className={'h-5 text-gray-300'} />
    );
};

export default AccountShareJobs;
